import { Link } from "react-router-dom";
import { Calendar, MapPin, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatEventDate } from "@/lib/utils";
import SmartImage from "@/components/SmartImage";

export default function EventCard({ event }) {
  const isFull = event.is_full ?? (event.spots_remaining === 0);
  const registrationOpen = event.registration_open !== false;
  const registrationClosed = !registrationOpen || isFull;
  const allowWaitlist = event.allow_waitlist_when_full !== false;
  const dateStr = formatEventDate(event.date, { weekday: "short", month: "long", day: "numeric" });

  // Auto pick "Sold Out" when at capacity or manually marked full; else "Registration Closed" when reg toggled off.
  const statusLabel = isFull ? "Sold out" : (!registrationOpen ? "Registration closed" : null);

  return (
    <div className="card-elegant overflow-hidden flex flex-col group" data-testid={`event-card-${event.event_id}`}>
      <Link to={`/events/${event.event_id}`} className="block relative aspect-[4/3] overflow-hidden">
        <SmartImage
          src={event.image_url}
          alt={event.title}
          className="w-full h-full transition-transform duration-500 group-hover:scale-105"
          style={{ objectPosition: `${event.image_focal_x ?? 50}% ${event.image_focal_y ?? 50}%` }}
        />
        <div className="absolute top-4 left-4 flex gap-2">
          <Badge className="bg-white/90 text-black hover:bg-white rounded-full">${(event.price_cents/100).toFixed(0)}</Badge>
          {event.category && <Badge className="bg-black/80 text-white hover:bg-black rounded-full" data-testid={`event-cat-${event.event_id}`}>{event.category}</Badge>}
        </div>
        {statusLabel && (
          <div className="absolute top-4 right-4">
            <Badge className="bg-black/80 text-white rounded-full" data-testid={`event-status-${event.event_id}`}>{statusLabel}</Badge>
          </div>
        )}
      </Link>
      <div className="p-6 flex-1 flex flex-col">
        <div className="text-eyebrow mb-2">Ages {event.age_min}–{event.age_max}</div>
        <h3 className="font-heading text-2xl leading-tight mb-3">{event.title}</h3>
        <div className="space-y-1.5 text-sm text-neutral-600 mb-6">
          <div className="flex items-center gap-2"><Calendar size={14} className="gold" /> {dateStr} · {event.time}</div>
          <div className="flex items-center gap-2"><MapPin size={14} className="gold" /> {event.venue}</div>
          {isFull && (
            <div className="flex items-center gap-2 text-neutral-500"><Users size={14} className="gold" /> Sold out</div>
          )}
        </div>
        <div className="mt-auto flex gap-3">
          <Link to={`/events/${event.event_id}`} className="flex-1" data-testid={`event-details-${event.event_id}`}>
            <Button variant="outline" className="w-full rounded-full">View details</Button>
          </Link>
          {registrationClosed ? (
            (isFull && allowWaitlist) ? (
              <Link to={`/events/${event.event_id}?waitlist=1`} data-testid={`event-waitlist-${event.event_id}`}>
                <Button variant="secondary" className="rounded-full">Waitlist</Button>
              </Link>
            ) : (
              <Button disabled variant="secondary" className="rounded-full" data-testid={`event-closed-${event.event_id}`}>
                {isFull ? "Sold out" : "Registration closed"}
              </Button>
            )
          ) : (
            <Link to={`/events/${event.event_id}`} data-testid={`event-buy-${event.event_id}`}>
              <Button className="rounded-full bg-black hover:bg-neutral-800 text-white">Buy ticket</Button>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
